import { BranchStateService, IdsAdapterService } from '@alpha-core/platform-ui';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, lastValueFrom, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class userService {
    private httpClient = inject(HttpClient);
    private router = inject(Router);
    userInfo: UserApiResponse | undefined;
    userPageSize!: number;
    usersMetadata: UserMetadata | null = null;
    idsAdapterService = inject(IdsAdapterService);

    private getUserInfo() {
        return this.httpClient.get<UserApiResponse>(`${environment.ALPHA_UI_ADMIN_SERVICE_URL}/user/user-info`).pipe(
            tap((userInfo) => {
                this.userInfo = userInfo;
            }),
            catchError((exception: HttpErrorResponse) => {
                if (exception.status === 401) {
                    if (exception?.error?.message === 'FORBIDDEN' || exception?.error?.error.message === 'FORBIDDEN') {
                        this.router.navigate(['unauthorized'], {
                            queryParams: { redirectUrl: encodeURIComponent(window.location.href) },
                        });
                        return of(null);
                    } else {
                        const redirectUrl = this.ensureBranchInUrl(window.location.href);
                        window.location.href = `${environment.ALPHA_UI_ADMIN_SERVICE_URL}/auth/login?redirectUrl=${encodeURIComponent(redirectUrl)}`;
                    }
                }
                throw exception;
            }),
        );
    }

    getGroupInfo() {
        return this.httpClient.get(`${environment.ALPHA_UI_ADMIN_SERVICE_URL}/auth/group-info`);
    }

    getUserMetadata() {
        if (!this.usersMetadata) {
            return this.httpClient.get<UserMetadata>(`${environment.ALPHA_UI_ADMIN_SERVICE_URL}/user/metadata`).pipe(
                tap((metadata) => {
                    this.usersMetadata = metadata;
                }),
            );
        }
        return of(this.usersMetadata);
    }

    updateUserMetadata(metadata: UserMetadata) {
        return this.httpClient
            .post(`${environment.ALPHA_UI_ADMIN_SERVICE_URL}/user/metadata`, {
                metadata,
            })
            .pipe(
                tap(() => {
                    if (this.usersMetadata) {
                        this.usersMetadata.metadata[0].metadata = metadata;
                    }
                    this.idsAdapterService.invalidateUserCache();
                }),
            );
    }

    getUser() {
        if (this.userInfo) {
            return of(this.userInfo);
        } else {
            return this.getUserInfo();
        }
    }

    updateUserToLocal() {
        if (!window.alpha?.local) {
            console.warn(new Error('alpha.local does not exist. Could not set alpha.local.userInfo'));
            return;
        }
        const localMappingSource = window.alpha.local.get();
        const userInfo = this.userInfo?.response;
        localMappingSource.userInfo = userInfo;
    }

    fetchUser(userName: string) {
        return this.httpClient.post<any>(`${environment.ALPHA_UI_ADMIN_SERVICE_URL}/user/fetch`, { userName });
    }

    fetchUserWithReportees(userName: string) {
        return this.httpClient.post<{ allUsersWithReporteeFlag: any[] }>(
            `${environment.ALPHA_UI_ADMIN_SERVICE_URL}/user/get-all-users-with-reportees`,
            { username: userName },
        );
    }

    getAncestors(userName: string) {
        return this.httpClient.post<{ ancestors: string[] }>(
            `${environment.ALPHA_UI_ADMIN_SERVICE_URL}/user/get-ancestors`,
            { username: userName },
        );
    }

    async createUser(userDetails: any) {
        try {
            const response = await lastValueFrom(
                this.httpClient.post(`${environment.ALPHA_UI_ADMIN_SERVICE_URL}/user/create`, userDetails),
            );
            this.idsAdapterService.invalidateUserCache();
            return response;
        } catch (error) {
            console.error('Failed to create user', error);
            throw error;
        }
    }

    async updateUser(userDetails: any) {
        try {
            const response = await lastValueFrom(
                this.httpClient.post(`${environment.ALPHA_UI_ADMIN_SERVICE_URL}/user/update`, userDetails),
            );
            this.idsAdapterService.invalidateUserCache();
            return response;
        } catch (error) {
            console.error('Failed to update user', error);
            throw error;
        }
    }

    async disableUser(userDetails: any) {
        try {
            const response = await lastValueFrom(
                this.httpClient.post(
                    `${environment.ALPHA_UI_ADMIN_SERVICE_URL}/user/disable`,
                    {
                        userId: userDetails.id,
                        userName: userDetails.user_id,
                    },
                    { withCredentials: true },
                ),
            );
            this.idsAdapterService.invalidateUserCache();
            return response;
        } catch (error) {
            console.error('Failed to disable user', error);
            throw error;
        }
    }

    logOut(path?: string) {
        const redirectUrl = this.ensureBranchInUrl(path || window.location.href);
        window.location.href = `${environment.ALPHA_UI_ADMIN_SERVICE_URL}/auth/logout?redirectUrl=${encodeURIComponent(redirectUrl)}`;
    }

    /**
     * Ensures the branch query param is present in the URL.
     * If branch param already exists, it's preserved. Otherwise, it's added.
     */
    private ensureBranchInUrl(url: string): string {
        try {
            const urlObj = new URL(url);
            if (!urlObj.searchParams.has('branch')) {
                const branch = BranchStateService.getBranchFromUrl();
                urlObj.searchParams.set('branch', branch);
            }
            return urlObj.toString();
        } catch {
            return url;
        }
    }

    fetchSessionMaxAge() {
        return this.httpClient.get<{ sessionMaxAge: number; sessionTimeout: number }>(
            `${environment.ALPHA_UI_ADMIN_SERVICE_URL}/auth/fetch-cookie-maxage`,
        );
    }
}

interface SubInfo {
    accountId: string;
    strategy: string;
}

interface Metadata {
    country: string;
    pincode: number;
}

interface Subscription {
    organisationId: string;
    type: string;
    start_date: string;
    expiry_date: string;
    maxNumberOfApps: string;
    disabled: boolean;
    status: string;
    description: string;
    ui_config: null;
}

interface AdditionalInfo {
    id: string;
    userKey: null;
    organisationId: string;
    createdAt: string;
    contactNumber: string;
    metadata: Metadata;
    invalidAttempt: string;
    tempPasswordFlag: boolean;
    publicUser: boolean;
    subscription: Subscription;
}

export interface UserInfo {
    sub: SubInfo;
    displayName: string;
    firstName: string;
    lastName: string;
    username: string;
    groupList: any[];
    organisationId: string;
    additional: AdditionalInfo;
    roles: any[];
    teams: any[];
}

export interface UserApiResponse {
    status: number;
    response: UserInfo;
}

interface UserMetadata {
    metadata: {
        enquiryInbox: any;
        savedFilters: ISavedFilters;
        defaultTask: any;
        enquiryDefaultTask: any;
        adminDefaultTask: any;
        [key: string]: any;
    };
}

// Root level interface to hold UUID-based keys
export interface ISavedFilters {
    [uuid: string]: ITaskContainer;
}

// Container for each task type under a specific UUID
export interface ITaskContainer {
    [taskType: string]: ITask[];
}

// Individual Task structure
export interface ITask {
    name: string;
    key: string;
    default: boolean;
    filterObject: IFilterObject;
}

// Filter Object containing multiple filters
export interface IFilterObject {
    [filterKey: string]: IFilter;
}

// Base interface for each filter item with common fields
export interface IFilter {
    type: string;
    mapping: string;
    mappingType: string;
    label: string;
    value?: string | null;
    minLabel?: string;
    maxLabel?: string;
    [additionalProperties: string]: any; // Allows additional fields like "New Case", "Pending Documents" for specific cases
}

export interface FetchAllUsersQueryDto {
    pageSize?: number;
    pageNumber?: number;
    search?: string;
}
